<template>
  <div class="fd-preview">
    <div class="fd-section-title">
      <feather-icon
        icon="EyeIcon"
        size="14"
        class="mr-50"
      />
      Превью формы
    </div>

    <div
      v-if="!hasColumns"
      class="fd-preview-empty"
    >
      Добавьте колонки в record-уровень чтобы увидеть превью
    </div>

    <div
      v-else
      class="fd-preview-table-wrap"
    >
      <table class="fd-preview-table">
        <!-- Header — column_heads -->
        <thead>
          <tr>
            <th
              v-for="(head, i) in columnHeads"
              :key="i"
              :class="{ 'fd-pv-right': isRecordColNumeric(i), 'fd-pv-spacer': isRecordColSpacer(i) }"
            >
              {{ head || '' }}
            </th>
          </tr>
        </thead>

        <tbody>
          <!-- Group levels — show structure with their own columns -->
          <template v-for="(gl, gi) in groupLevels">
            <tr
              :key="'g-' + gi"
              class="fd-pv-group"
            >
              <td
                v-for="(col, ci) in alignedGroupColumns(gl)"
                :key="ci"
                :class="{ 'fd-pv-right': col._numeric, 'fd-pv-spacer': col.type === 'spacer' }"
              >
                <template v-if="col._isName">
                  <span :style="{ paddingLeft: (gi * 16) + 'px' }">
                    ▸ <em>{{ groupLabel(gl) }}</em>
                  </span>
                </template>
                <template v-else-if="col.type === 'aggr_sum'">
                  <span class="fd-pv-aggr">Σ {{ entName(col.destinationEntityId) }}</span>
                </template>
                <template v-else-if="col.type === 'scalar'">
                  <span class="fd-pv-scalar">f({{ col.scalarId || '?' }})</span>
                </template>
              </td>
            </tr>
          </template>

          <!-- Record row — show field names + editable markers -->
          <tr
            v-if="recordLevel"
            class="fd-pv-record"
          >
            <td
              v-for="(col, ci) in recordColumns"
              :key="ci"
              :class="{ 'fd-pv-right': col.align === 'right', 'fd-pv-spacer': col.type === 'spacer', 'fd-pv-cell-edit': col.editable }"
            >
              <template v-if="col.type === 'spacer'" />
              <template v-else>
                <span :class="{ 'fd-pv-editable': col.editable, 'fd-pv-readonly': !col.editable }">
                  {{ entName(col.entityId || col.destinationEntityId) }}
                </span>
              </template>
            </td>
          </tr>
          <!-- Second record row (placeholder) -->
          <tr
            v-if="recordLevel"
            class="fd-pv-record fd-pv-record--dim"
          >
            <td
              v-for="(col, ci) in recordColumns"
              :key="ci"
              :class="{ 'fd-pv-right': col.align === 'right', 'fd-pv-spacer': col.type === 'spacer' }"
            >
              <template v-if="col.type !== 'spacer'">
                ···
              </template>
            </td>
          </tr>

          <!-- + New record -->
          <tr
            v-if="recordLevel && recordLevel.addrecord"
            class="fd-pv-newrow"
          >
            <td :colspan="columnHeads.length">
              <feather-icon
                icon="PlusIcon"
                size="12"
              /> Новая запись
            </td>
          </tr>

          <!-- Grand Total — only columns with sumrows -->
          <tr
            v-if="hasGrandTotal"
            class="fd-pv-grand"
          >
            <td
              v-for="(col, ci) in recordColumns"
              :key="ci"
              :class="{ 'fd-pv-right': col.align === 'right', 'fd-pv-spacer': col.type === 'spacer' }"
            >
              <template v-if="ci === 0 && !isRecordColNumeric(0)">
                <strong>Итого</strong>
              </template>
              <template v-else-if="hasGrandTotalAt(ci)">
                <strong>Σ</strong>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CbsFormDesignerPreview',
  props: {
    levels: { type: Array, default: () => [] },
    columnHeads: { type: Array, default: () => [] },
    entityMap: { type: Object, default: () => ({}) },
    hasGrandTotal: { type: Boolean, default: true },
    grandTotalColumns: { type: Array, default: () => [] },
  },
  computed: {
    groupLevels() {
      return this.levels.filter(l => l.type !== 'record')
    },
    recordLevel() {
      return this.levels.find(l => l.type === 'record') || null
    },
    recordColumns() {
      return this.recordLevel ? this.recordLevel.columns : []
    },
    hasColumns() {
      return this.columnHeads.length > 0 && this.columnHeads.some(h => h)
    },
  },
  methods: {
    entName(id) {
      if (!id) return ''
      const info = this.entityMap[id]
      return info ? info.name : `#${id}`
    },

    groupLabel(level) {
      if (level.results && level.results.length > 0) {
        return this.entName(level.results[0].destinationEntityId) || 'Группа'
      }
      return 'Группа'
    },

    // Align group columns to match record columns count
    alignedGroupColumns(gl) {
      const recCols = this.recordColumns
      if (recCols.length === 0) return gl.columns
      // If group has same number of columns — use them directly
      if (gl.columns.length === recCols.length) {
        return gl.columns.map((col, i) => ({
          ...col,
          _isName: i === 0 && (col.entityId || col.type === 'data'),
          _numeric: col.type === 'aggr_sum' || col.type === 'scalar' || col.align === 'right',
        }))
      }
      // Otherwise map: first col = name, rest = match record positions
      return recCols.map((rc, i) => {
        if (i === 0) return { _isName: true, type: 'data' }
        // Find matching aggr_sum in group by destinationEntityId
        const aggrCol = gl.columns.find(c => c.type === 'aggr_sum' && c.destinationEntityId === (rc.entityId || rc.destinationEntityId))
        const scalarCol = gl.columns.find(c => c.type === 'scalar' && c.destinationEntityId === (rc.entityId || rc.destinationEntityId))
        if (aggrCol) return { ...aggrCol, _numeric: true }
        if (scalarCol) return { ...scalarCol, _numeric: true }
        return { type: rc.type === 'spacer' ? 'spacer' : 'empty', _numeric: false }
      })
    },

    isRecordColNumeric(ci) {
      const col = this.recordColumns[ci]
      return col && (col.align === 'right' || col.type === 'aggr_sum' || col.type === 'scalar')
    },

    isRecordColSpacer(ci) {
      const col = this.recordColumns[ci]
      return col && col.type === 'spacer'
    },

    hasGrandTotalAt(ci) {
      // Check from grandTotalColumns prop
      if (this.grandTotalColumns.length > 0) {
        const gtc = this.grandTotalColumns[ci]
        return gtc && gtc.type === 'sumrows'
      }
      // Fallback: only numeric record columns
      return this.isRecordColNumeric(ci)
    },
  },
}
</script>

<style scoped>
.fd-preview { margin-top: 16px; border-top: 1px solid #eee; padding-top: 12px; }
.fd-preview-empty { text-align: center; padding: 20px; color: #999; font-size: 12px; }
.fd-preview-table-wrap { overflow-x: auto; border: 1px solid #e0e0e0; border-radius: 6px; }
.fd-preview-table { width: 100%; font-size: 12px; border-collapse: collapse; }
.fd-preview-table th { padding: 7px 10px; background: #f5f5f5; border-bottom: 2px solid #ddd; font-weight: 600; font-size: 11px; text-align: left; white-space: nowrap; color: #555; }
.fd-preview-table td { padding: 5px 10px; border-bottom: 1px solid #f0f0f0; font-size: 12px; }
.fd-pv-group td { background: #f8f9fa; font-weight: 500; color: #444; border-bottom: 1px solid #e8e8e8; }
.fd-pv-aggr { color: #c62828; font-size: 11px; }
.fd-pv-scalar { color: #6a1b9a; font-size: 11px; }
.fd-pv-record td { color: #555; }
.fd-pv-record--dim td { color: #ccc; }
.fd-pv-editable { border-bottom: 1px dashed #7367f0; color: #333; }
.fd-pv-readonly { color: #999; }
.fd-pv-cell-edit { background: #fffef5; }
.fd-pv-newrow td { color: #7367f0; font-size: 11px; padding: 6px 10px; border-bottom: 1px solid #eee; }
.fd-pv-grand td { background: #f0eeff; border-top: 2px solid #7367f0; font-weight: 700; color: #333; }
.fd-pv-right { text-align: right; }
.fd-pv-spacer { width: 4px; max-width: 4px; padding: 0 2px !important; }
</style>
